import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

import { supabase } from '../lib/supabase';
import { QuotaChip } from '../components/billing/QuotaChip';
import { GenufitDirectionCWordmark } from '../components/GenufitDirectionCWordmark';
import { GenufitMark } from '../components/GenufitMark';
import { isResumeOnlyMode } from '../lib/resumeOnlyMode';
import { SidebarTooltip } from './SidebarTooltip';
import { useSidebarPrefs } from './useSidebarPrefs';

export interface MenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  subItems?: SubMenuItem[];
  matchPattern?: (pathname: string, searchParams: URLSearchParams) => boolean;
}

export interface SubMenuItem {
  id: string;
  label: string;
  path: string;
  matchPattern?: (pathname: string) => boolean;
}

export interface NavigationProps {
  menuItems?: MenuItem[];
  userInitials?: string;
  onSignOut?: () => void;
}

// Default menu items configuration
const defaultMenuItems: MenuItem[] = [
  {
    id: 'jobs',
    label: 'Jobs',
    path: '/',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
      </svg>
    ),
    matchPattern: (pathname, searchParams) => {
      const hasSearchQueryId = searchParams.has('searchQueryId');
      return (pathname === '/' || pathname.startsWith('/job/')) && !hasSearchQueryId;
    },
    subItems: [
      {
        id: 'jobs-list',
        label: 'Jobs',
        path: '/',
        matchPattern: (pathname) => pathname === '/' || pathname.startsWith('/job/'),
      },
    ],
  },
  {
    id: 'search-queries',
    label: 'Search Queries',
    path: '/search-queries',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    matchPattern: (pathname, searchParams) => {
      return pathname === '/search-queries' || searchParams.has('searchQueryId');
    },
  },
  {
    id: 'resumes',
    label: 'Resumes',
    path: '/resumes',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    matchPattern: (pathname) => pathname === '/resumes' || pathname.startsWith('/resume/'),
    subItems: [
      {
        id: 'resumes-list',
        label: 'Resumes',
        path: '/resumes',
        matchPattern: (pathname) => pathname === '/resumes' || pathname.startsWith('/resume/'),
      },
    ],
  },
  {
    id: 'ats-playground',
    label: 'ATS Playground',
    path: '/ats-playground',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    path: '/onboarding',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    matchPattern: (pathname) => pathname === '/onboarding',
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    subItems: [
      {
        id: 'settings-date',
        label: 'Date Settings',
        path: '/settings#date',
      },
      {
        id: 'settings-relevance',
        label: 'Relevance Analysis Rules',
        path: '/settings#relevance',
      },
      {
        id: 'settings-ai-memory',
        label: 'AI Memory',
        path: '/settings#ai-memory',
      },
      {
        id: 'settings-sidebar',
        label: 'Sidebar',
        path: '/settings#sidebar',
      },
    ],
  },
];

// Shared utility functions for navigation logic
const isItemActive = (
  item: MenuItem,
  pathname: string,
  searchParams: URLSearchParams
): boolean => {
  // Use matchPattern if provided
  if (item.matchPattern) {
    return item.matchPattern(pathname, searchParams);
  }
  // Fallback to path matching
  return pathname === item.path;
};

const isSubItemActive = (subItem: SubMenuItem, pathname: string, hash?: string): boolean => {
  if (subItem.matchPattern) {
    return subItem.matchPattern(pathname);
  }
  // Check if path matches and hash matches (for Settings tabs)
  if (subItem.path.includes('#')) {
    const [path, expectedHash] = subItem.path.split('#');
    const currentHash = hash || window.location.hash.slice(1);
    return pathname === path && (currentHash === expectedHash || (!currentHash && expectedHash === 'date'));
  }
  return pathname === subItem.path;
};

const getActiveMenuItem = (
  menuItems: MenuItem[],
  pathname: string,
  searchParams: URLSearchParams
): MenuItem | null => {
  for (const item of menuItems) {
    // Check if current path matches the main item
    if (isItemActive(item, pathname, searchParams)) {
      return item;
    }
    // Check sub-items
    if (item.subItems) {
      for (const subItem of item.subItems) {
        const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
        if (isSubItemActive(subItem, pathname, hash)) {
          return item;
        }
      }
    }
  }
  return null;
};

export const Navigation = ({ 
  menuItems: _menuItems = defaultMenuItems, 
  userInitials: _userInitials = 'RM',
  onSignOut: _onSignOut 
}: NavigationProps) => {
  // This component is not meant to be rendered directly
  // It's exported for Storybook stories that need the full navigation context
  return null;
};

const resumeOnlyMenuItems = defaultMenuItems.filter(item => item.path === '/resumes');

export function getNavigationMenuItems(): MenuItem[] {
  return isResumeOnlyMode() ? resumeOnlyMenuItems : defaultMenuItems;
}

const SIDEBAR_ICON_SLOT_CLASS = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-md';
const SIDEBAR_LABEL_CLASS =
  'min-w-0 truncate text-body font-medium overflow-hidden whitespace-nowrap transition-opacity duration-slow ease-in-out';

function sidebarRowClass(isExpanded: boolean): string {
  const grid = isExpanded
    ? 'grid grid-cols-[44px_minmax(0,1fr)] gap-x-3'
    : 'grid grid-cols-[44px_0fr] gap-x-0';
  return `${grid} h-11 w-full shrink-0 items-center rounded-md p-0 pl-2.5 pr-3 text-left font-inherit transition-[grid-template-columns,gap] duration-slow ease-in-out`;
}

function sidebarLogoRowClass(isExpanded: boolean): string {
  const grid = isExpanded
    ? 'grid grid-cols-[44px_minmax(0,1fr)] gap-x-3'
    : 'grid grid-cols-[44px_0fr] gap-x-0';
  return `${grid} h-[var(--top-nav-height)] min-h-[var(--top-nav-height)] max-h-[var(--top-nav-height)] w-full shrink-0 items-center border-b border-border pl-2.5 pr-3 transition-[grid-template-columns,gap] duration-slow ease-in-out`;
}

function sidebarLabelClass(isExpanded: boolean, isActive: boolean): string {
  const tone = isActive ? 'text-brand-primary' : 'text-text-primary';
  const visibility = isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none';
  return `${SIDEBAR_LABEL_CLASS} ${tone} ${visibility}`;
}

function sidebarToggleLabelClass(isExpanded: boolean): string {
  const visibility = isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none';
  return `${SIDEBAR_LABEL_CLASS} text-text-secondary ${visibility}`;
}

/** Profile row: same grid as nav rows for label alignment, no visible link chrome. */
function sidebarProfileRowClass(isExpanded: boolean): string {
  const grid = isExpanded
    ? 'grid grid-cols-[44px_minmax(0,1fr)] gap-x-3'
    : 'grid grid-cols-[44px_0fr] gap-x-0';
  return `${grid} mb-4 mt-auto w-full shrink-0 items-center border-0 bg-transparent p-0 pl-2.5 pr-3 outline-none focus:outline-none focus-visible:outline-none focus-visible:[&>span:first-child]:ring-2 focus-visible:[&>span:first-child]:ring-interactive-focus-ring`;
}

function CollapseChevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      className="h-[18px] w-[18px] shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      {expanded ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      )}
    </svg>
  );
}

// Sidebar Component - Left Menu with Main Items
export const NavigationSidebar = ({
  menuItems = getNavigationMenuItems(),
  userInitials = 'RM'
}: Omit<NavigationProps, 'onSignOut'>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isExpanded,
    isPinnedExpanded,
    canExpand,
    toggleExpanded,
    onSidebarMouseEnter,
    onSidebarMouseLeave,
  } = useSidebarPrefs();

  const sidebarWidthClass = isExpanded ? 'w-sidebar-expanded' : 'w-sidebar';
  const showCollapsedTooltips = !isExpanded;

  return (
    <nav
      aria-expanded={isExpanded}
      onMouseEnter={onSidebarMouseEnter}
      onMouseLeave={onSidebarMouseLeave}
      className={`${sidebarWidthClass} flex h-full shrink-0 flex-col overflow-hidden border-r border-border bg-surface-default transition-[width] duration-slow ease-in-out`}
      data-testid="navigation-sidebar"
    >
      <div data-testid="sidebar-logo-row" className={sidebarLogoRowClass(isExpanded)}>
        <span className="flex h-11 w-11 shrink-0 items-center justify-center">
          <GenufitMark size={32} className="shrink-0" />
        </span>
        <GenufitDirectionCWordmark
          variant="sidebar"
          className={`min-w-0 transition-opacity duration-slow ease-in-out ${
            isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        />
      </div>

      <div className="flex flex-1 flex-col gap-1 py-4">
        {menuItems.map((item) => {
          const isActive = isItemActive(item, location.pathname, searchParams);
          const link = (
            <a
              href={item.path}
              onClick={(e) => {
                e.preventDefault();
                navigate(item.path);
              }}
              className={`${sidebarRowClass(isExpanded)} transition-colors duration-fast ${
                isActive ? 'bg-brand-primary-subtle' : 'bg-transparent hover:bg-surface-muted'
              }`}
            >
              <span
                className={`${SIDEBAR_ICON_SLOT_CLASS} ${
                  isActive ? 'text-brand-primary' : 'text-text-primary'
                }`}
              >
                {item.icon}
              </span>
              <span className={sidebarLabelClass(isExpanded, isActive)}>{item.label}</span>
            </a>
          );

          return (
            <SidebarTooltip key={item.id} label={item.label} enabled={showCollapsedTooltips}>
              {link}
            </SidebarTooltip>
          );
        })}

        {canExpand && (
          <div className="mt-2 w-full">
            <button
              type="button"
              onClick={toggleExpanded}
              aria-label={isPinnedExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              title={isPinnedExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              data-testid="sidebar-toggle"
              className={`${sidebarRowClass(isExpanded)} border-0 bg-transparent transition-colors duration-fast hover:bg-surface-muted`}
            >
              <span className={`${SIDEBAR_ICON_SLOT_CLASS} text-text-secondary`}>
                <CollapseChevron expanded={isPinnedExpanded} />
              </span>
              <span className={sidebarToggleLabelClass(isExpanded)}>
                {isPinnedExpanded ? 'Collapse' : 'Expand'}
              </span>
            </button>
          </div>
        )}
      </div>

      <SidebarTooltip label="Profile" enabled={showCollapsedTooltips}>
        <a
          href="/profile"
          data-testid="sidebar-profile-link"
          onClick={(e) => {
            e.preventDefault();
            navigate('/profile');
          }}
          className={sidebarProfileRowClass(isExpanded)}
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-surface-muted text-xs font-medium text-text-secondary">
            {userInitials}
          </span>
          <span className={sidebarLabelClass(isExpanded, false)}>Profile</span>
        </a>
      </SidebarTooltip>
    </nav>
  );
};

// Top Navigation Component - Sub-items
export const NavigationTop = ({
  menuItems = getNavigationMenuItems(),
  onSignOut
}: Omit<NavigationProps, 'userInitials'>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const activeMenuItem = getActiveMenuItem(menuItems, location.pathname, searchParams);
  const activeSubItems = activeMenuItem?.subItems || [];

  const handleSignOut = async () => {
    if (onSignOut) {
      onSignOut();
    } else {
      await supabase.auth.signOut();
      navigate('/login');
    }
  };

  return (
    <div className="border-b border-border bg-surface-default">
      <div className="flex h-[var(--top-nav-height)] items-center justify-between px-6">
          <div className="flex items-center gap-6">
            {activeSubItems.length > 0 ? (
              activeSubItems.map((subItem) => {
                const isActive = isSubItemActive(subItem, location.pathname);
                return (
                  <a
                    key={subItem.id}
                    href={subItem.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(subItem.path);
                    }}
                    className={`pb-2 px-1 border-b-2 font-semibold text-body transition-colors duration-fast ${
                      isActive
                        ? 'border-brand-primary text-brand-primary hover:text-brand-primary-hover'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {subItem.label}
                  </a>
                );
              })
            ) : (
              activeMenuItem && (
                <a
                  href={activeMenuItem.path}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(activeMenuItem.path);
                  }}
                  className="pb-2 px-1 border-b-2 border-brand-primary text-brand-primary font-semibold text-body"
                >
                  {activeMenuItem.label}
                </a>
              )
            )}
          </div>
          <div className="flex items-center gap-3">
            <QuotaChip />
            <button
              onClick={handleSignOut}
              className="px-3 py-1 text-body text-text-secondary hover:text-text-primary hover:bg-surface-muted rounded-md transition-colors duration-fast"
            >
              Sign out
            </button>
          </div>
      </div>
    </div>
  );
};
